import React, {
  RefObject,
  forwardRef,
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import styled, { css } from "styled-components";
import { LocationDescriptor } from "history";
import Colors from "../theme/Colors";
import { useTracking } from "../../Tracking";
import LoadingText from "../LoadingText";
import { TrackEventProps } from "@teamscheme/tracking";

export interface LinkBaseProps {
  trackEvent?: TrackEventProps;
  children: React.ReactNode;
  isLoading?: boolean;
  disabled?: boolean;
  name?: string;
  block?: boolean;
}

export type BaseAnchorProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "children"
>;

export type BaseButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
>;

export interface AnchorProps extends LinkBaseProps, BaseAnchorProps {
  href: string;
}

export interface RouterLinkProps extends LinkBaseProps, BaseAnchorProps {
  linkTo: LocationDescriptor<any>;
}

export interface ButtonProps extends LinkBaseProps, BaseButtonProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export interface FormButtonProps extends LinkBaseProps, BaseButtonProps {
  type: "submit" | "button";
}

const commonStyles = css`
  outline: none;
  border: none;
  background: none;
  text-decoration: underline;
  padding: 0;
  font-size: 1em;
  cursor: pointer;
  color: #1877ba;
  min-height: auto;

  &:hover {
    color: #3a5ca8;
  }

  &[disabled],
  &[data-disabled] {
    background: none;
    color: ${Colors.disabled};
    text-decoration: none;
  }

  &:not([disabled]):not([data-disabled]):hover,
  &:not([disabled]):not([data-disabled]):active {
    transform: none;
  }

  &:focus {
    box-shadow: none;
    outline: 3px dotted ${Colors.highlight};
    outline-offset: 1px;
  }
`;

function toggledStyles(props: LinkBaseProps) {
  return [
    props.block &&
      css`
        display: flex;
        width: 100%;
      `,
  ];
}

const removeBlock = {
  shouldForwardProp: (prop: any) => (prop as string) !== "block",
};

const StyledButton = styled.button.withConfig(removeBlock)`
  ${commonStyles}

  ${(props: any) => toggledStyles(props)}
`;

const StyleAnchor = styled.a.withConfig(removeBlock)`
  ${commonStyles}

  ${(props: any) => toggledStyles(props)}
`;

const StyledRouterLink = styled(RouterLink).withConfig(removeBlock)`
  ${commonStyles}

  ${(props: any) => toggledStyles(props)}
`;

export type LinkProps =
  | AnchorProps
  | ButtonProps
  | FormButtonProps
  | RouterLinkProps;

const Link = forwardRef<HTMLButtonElement | HTMLAnchorElement, LinkProps>(
  function (props, ref) {
    const tracking = useTracking();
    const {
      children,
      isLoading,
      disabled: disabledProp,
      trackEvent,
      ...rest
    } = props;

    const content = isLoading ? <LoadingText /> : children;
    const disabled = isLoading || disabledProp;

    function track() {
      if (trackEvent) {
        tracking.event({
          ...trackEvent,
          label: trackEvent.label || children?.toString() || undefined,
        });
      }
    }

    if ("href" in rest) {
      const { href, onClick, ...other } = rest;

      return (
        <StyleAnchor
          ref={ref as RefObject<HTMLAnchorElement>}
          href={!disabled ? href : undefined}
          data-disabled={disabled}
          onClick={(e) => {
            track();
            if (onClick) onClick(e);
          }}
          {...other}
        >
          {content}
        </StyleAnchor>
      );
    }

    if ("linkTo" in rest) {
      const { linkTo, onClick, ...other } = rest;

      return disabled ? (
        <StyleAnchor
          ref={ref as RefObject<HTMLAnchorElement>}
          href={undefined}
          data-disabled={true}
          {...other}
        >
          {content}
        </StyleAnchor>
      ) : (
        <StyledRouterLink
          ref={ref as RefObject<HTMLAnchorElement>}
          to={linkTo}
          onClick={(e) => {
            track();
            if (onClick) onClick(e);
          }}
          {...other}
        >
          {content}
        </StyledRouterLink>
      );
    }

    if ("onClick" in rest) {
      const { onClick, ...other } = rest;

      return (
        <StyledButton
          ref={ref as RefObject<HTMLButtonElement>}
          onClick={(e) => {
            track();
            if (onClick) onClick(e);
          }}
          disabled={disabled}
          {...other}
        >
          {content}
        </StyledButton>
      );
    }

    const { type, onClick, ...other } = rest;

    return (
      <StyledButton
        ref={ref as RefObject<HTMLButtonElement>}
        type={type}
        disabled={disabled}
        onClick={(e) => {
          track();
          if (onClick) onClick(e);
        }}
        {...other}
      >
        {content}
      </StyledButton>
    );
  }
);

export default Link;
